import EventBus from '../EventBus';
import { GameEvents } from '../GameEvents';
import type { UiState } from '../GameManager';
import getLogger from '../logger';

// Static asset imports for rendering
import grassImg from '../../../assets/images/grass.png';
import waterImg from '../../../assets/images/water.png';
import shrubImg from '../../../assets/images/shrub.png';
import playerImg from '../../../assets/images/PlayerPlaceholder.png';
import bugImg from '../../../assets/images/demented-spider.png';
import bugGreyImg from '../../../assets/images/demented-spider-grey.png';
import rhinoBeetleImg from '../../../assets/images/rhino_beetle.png';
import stumpyImg from '../../../assets/images/demented-stump.png';
import geckoImg from '../../../assets/images/gecko.png';

const logger = getLogger('SpatialHandler');

const assetManifest: Record<string, { key: string; url: string }> = {
  grass: { key: 'grass', url: grassImg },
  water: { key: 'water', url: waterImg },
  tree: { key: 'tree', url: shrubImg },
  player: { key: 'player', url: playerImg },
  bug: { key: 'bug', url: bugImg },
  bug_grey: { key: 'bug_grey', url: bugGreyImg },
  rhino_beetle: { key: 'rhino_beetle', url: rhinoBeetleImg },
  stumpy: { key: 'stumpy', url: stumpyImg },
  gecko: { key: 'gecko', url: geckoImg },
};

const themes: Record<string, string[]> = {
  default: ['grass', 'water', 'tree', 'player', 'bug', 'bug_grey', 'rhino_beetle', 'stumpy', 'gecko'],
};

/**
 * SpatialHandler owns the spatial slice of UI state AND all Phaser/rendering lifecycle.
 *
 * It subscribes to spatial events from the game logic (LevelManager, systems)
 * and updates the spatial state accordingly. The Scene layer reads this state
 * reactively to render visual elements.
 *
 * It also manages the Phaser.Game instance and LevelScene lifecycle, completely
 * isolating rendering concerns from game logic.
 */
export default class SpatialHandler {
  private eventBus: EventBus;
  private state: Readonly<UiState>;
  private slice: UiState['spatial'];

  // Phaser lifecycle management
  private phaserGame: any = null;
  private phaserCanvasElement: HTMLElement | null = null;
  private phaserCanvasSize: { width: number; height: number } | null = null;

  constructor(state: Readonly<UiState>, slice: UiState['spatial'], eventBus: EventBus) {
    this.state = state;
    this.slice = slice;
    this.eventBus = eventBus;

    // Subscribe to Phaser canvas lifecycle
    this.eventBus.on(GameEvents.PHASER_CANVAS_READY, (payload: any) =>
      this._onPhaserCanvasReady(payload)
    );
    this.eventBus.on(GameEvents.PHASER_CANVAS_DESTROYED, () =>
      this._onPhaserCanvasDestroyed()
    );

    // Subscribe to scene preparation - create/start scene
    this.eventBus.on(GameEvents.PREPARE_SCENE, (payload: any) =>
      this._onPrepareScene(payload)
    );

    // Subscribe to highlight events
    this.eventBus.on(GameEvents.UPDATE_PLAYER_ACTION_HIGHLIGHTS, (payload: any) =>
      this._onUpdatePlayerActionHighlights(payload)
    );
    this.eventBus.on(GameEvents.CLEAR_PLAYER_ACTION_HIGHLIGHTS, () =>
      this._onClearPlayerActionHighlights()
    );
    this.eventBus.on(GameEvents.UPDATE_ENEMY_PATTERN_HIGHLIGHTS, (payload: any) =>
      this._onUpdateEnemyPatternHighlights(payload)
    );
    this.eventBus.on(GameEvents.CLEAR_ENEMY_PATTERN_HIGHLIGHTS, () =>
      this._onClearEnemyPatternHighlights()
    );

    // Subscribe to intention indicator events
    this.eventBus.on(GameEvents.UPDATE_INTENTION_INDICATORS, (payload: any) =>
      this._onUpdateIntentionIndicators(payload)
    );
    this.eventBus.on(GameEvents.CLEAR_INTENTION_INDICATORS, () =>
      this._onClearIntentionIndicators()
    );

    // Subscribe to terrain and character events
    this.eventBus.on(GameEvents.UPDATE_TERRAIN, (payload: any) =>
      this._onUpdateTerrain(payload)
    );
    this.eventBus.on(GameEvents.UPDATE_CHARACTERS, (payload: any) =>
      this._onUpdateCharacters(payload)
    );

    // Subscribe to animation events
    this.eventBus.on(GameEvents.START_ANIMATIONS, (payload: any) =>
      this._onStartAnimations(payload)
    );
    this.eventBus.on(GameEvents.ANIMATIONS_COMPLETE, () =>
      this._onAnimationsComplete()
    );
  }

  private _onUpdatePlayerActionHighlights({ hexCoords }: { hexCoords: Array<{ q: number; r: number }> }) {
    logger.debug('Updating player action highlights', hexCoords.length);
    this.slice.highlights.playerActions = hexCoords;
  }

  private _onClearPlayerActionHighlights() {
    logger.debug('Clearing player action highlights');
    this.slice.highlights.playerActions = [];
  }

  private _onUpdateEnemyPatternHighlights({ hexCoords }: { hexCoords: Array<{ q: number; r: number }> }) {
    logger.debug('Updating enemy pattern highlights', hexCoords.length);
    this.slice.highlights.enemyPattern = hexCoords;
  }

  private _onClearEnemyPatternHighlights() {
    logger.debug('Clearing enemy pattern highlights');
    this.slice.highlights.enemyPattern = null;
  }

  private _onUpdateIntentionIndicators({ indicators }: { indicators: UiState['spatial']['intentionIndicators'] }) {
    logger.debug('Updating intention indicators', indicators.length);
    this.slice.intentionIndicators = indicators;
  }

  private _onClearIntentionIndicators() {
    logger.debug('Clearing intention indicators');
    this.slice.intentionIndicators = [];
  }

  private _onUpdateTerrain({ terrain }: { terrain: Array<{ entityId: string; coords: { q: number; r: number }; spriteKey: string }> }) {
    logger.debug('Updating terrain', terrain.length);
    this.slice.terrain = terrain;
  }

  private _onUpdateCharacters({ characters }: { characters: UiState['spatial']['characters'] }) {
    logger.debug('Updating characters', characters.length);
    this.slice.characters = characters;
  }

  private _onStartAnimations({ plan, resolve }: { plan: any[][]; resolve: () => void }) {
    logger.debug('Starting animations', plan.length);
    this.slice.pendingAnimations = { plan, resolve };
  }

  private _onAnimationsComplete() {
    logger.debug('Animations complete');
    if (this.slice.pendingAnimations?.resolve) {
      this.slice.pendingAnimations.resolve();
    }
    this.slice.pendingAnimations = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phaser Lifecycle Management
  // ═══════════════════════════════════════════════════════════════════════════

  private _onPhaserCanvasReady({ element, width, height }: { element: HTMLElement; width: number; height: number }) {
    logger.debug('Phaser canvas ready', { width, height });

    // Store canvas info for later Phaser.Game creation
    this.phaserCanvasElement = element;
    this.phaserCanvasSize = { width, height };

    // Create Phaser.Game instance (dynamic require to avoid loading Phaser in tests)
    const Phaser = require('phaser');
    this.phaserGame = new Phaser.Game({
      type: Phaser.AUTO,
      width,
      height,
      parent: element,
      transparent: true,
      scene: [],  // Scenes added later when levels start
    });
  }

  private _onPhaserCanvasDestroyed() {
    logger.debug('Phaser canvas destroyed');

    if (this.phaserGame) {
      this.phaserGame.destroy(true);
      this.phaserGame = null;
    }

    this.phaserCanvasElement = null;
    this.phaserCanvasSize = null;
  }

  private _onPrepareScene({ key, data, themeName }: { key: string; data: any; themeName: string }) {
    // Clear stale spatial data before creating the new scene.
    // When textures are cached from a previous level, Phaser may run create()
    // synchronously during scene.start(), before _onStartLevel populates new
    // entity data. Without this reset, the new scene's watchers would fire with
    // old characters, creating sprites with wrong textures for reused entity IDs.
    this.slice.characters = [];
    this.slice.terrain = [];
    this.slice.highlights = { playerActions: [], enemyPattern: null };
    this.slice.intentionIndicators = [];
    this.slice.pendingAnimations = null;

    if (!this.phaserGame) {
      logger.error('PREPARE_SCENE fired before Phaser canvas was ready');
      return;
    }

    logger.debug('Preparing scene for level', key);

    // Clean up previous scene
    if (this.phaserGame.scene.keys.level) {
      this.phaserGame.scene.remove('level');
    }

    // Prepare assets based on theme
    const assetKeys = themes[themeName] || themes.default;
    const assets = assetKeys.map(assetKey => assetManifest[assetKey]);

    // Create and add LevelScene (dynamic require to avoid loading Phaser in tests)
    const LevelScene = require('../../scenes/LevelScene').default;
    const levelScene = new LevelScene(this.eventBus.getEmitter(), this.state);

    this.phaserGame.scene.add('level', levelScene, false);
    this.phaserGame.scene.start('level', { key, data, assets });
  }
}
